import React, { useContext, useState } from 'react';
import { BasicModalWithFooter, InfoBadge, TextLarge, Title5 } from '@hz-design-system/web-ui';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import EnvironmentContext from '../../../../contexts/EnvironmentContext';
import Classes from './MotorsTourModal.module.scss';

const SLIDE_NAMES = ['newFeatures', 'reviews', 'badge', 'services'] as const;
type Slide = (typeof SLIDE_NAMES)[number];

const MotorsTourModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useI18nContext();
  const { staticBaseUrl } = useContext(EnvironmentContext);
  const [currentSlide, setCurrentSlide] = useState(0);

  const totalSlides = SLIDE_NAMES.length;
  const isFirst = currentSlide === 0;
  const isLast = currentSlide === totalSlides - 1;

  const goNext = () => setCurrentSlide((prev) => Math.min(prev + 1, totalSlides - 1));
  const goPrev = () => setCurrentSlide((prev) => Math.max(prev - 1, 0));

  const renderSlideMedia = (name: Slide, i: number) => {
    const imageAlt = t(`motorsTour.slides.${name}.imageAlt`);
    const slideNumber = i + 1;

    return (
      <video className={Classes.slideImage} autoPlay muted loop playsInline preload="metadata" aria-label={imageAlt}>
        <source src={`${staticBaseUrl}images/motors-tour-slide-${slideNumber}.webm`} type="video/webm" />
        <source src={`${staticBaseUrl}images/motors-tour-slide-${slideNumber}.mp4`} type="video/mp4" />
      </video>
    );
  };

  const getFooterButtons = () => {
    if (isFirst) {
      return [
        {
          component: 'PrimaryButton' as const,
          text: t('motorsTour.slides.newFeatures.cta'),
          onClick: goNext,
        },
      ];
    }
    if (isLast) {
      return [
        {
          component: 'PrimaryButton' as const,
          text: t('motorsTour.close'),
          onClick: onClose,
        },
      ];
    }
    return [
      {
        component: 'SecondaryButton' as const,
        text: t('motorsTour.previous'),
        onClick: goPrev,
      },
      {
        component: 'PrimaryButton' as const,
        text: t('motorsTour.next'),
        onClick: goNext,
      },
    ];
  };

  const currentSlideName = SLIDE_NAMES[currentSlide];

  return (
    <BasicModalWithFooter
      isOpen
      size="s"
      onClose={onClose}
      contentClassName={Classes.content}
      buttonsAlignmentMobile={isFirst || isLast ? 'stacked' : 'stretched'}
      buttonsAlignmentDesktop={isFirst || isLast ? 'stacked' : 'rightAligned'}
      buttons={getFooterButtons()}
      ariaLabel={t('motorsTour.slides.newFeatures.description')}
    >
      <div className={Classes.slideContainer} aria-live="polite" aria-atomic="true">
        <div className={Classes.slideTrack} style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
          {SLIDE_NAMES.map((name, i) => (
            <div key={name} className={Classes.slide} aria-hidden={i !== currentSlide}>
              {renderSlideMedia(name, i)}
            </div>
          ))}
        </div>
      </div>

      <div className={Classes.textContent}>
        <div className={Classes.title}>
          <Title5>{t(`motorsTour.slides.${currentSlideName}.title`)}</Title5>
          {isFirst && <InfoBadge>{t('motorsTour.new')}</InfoBadge>}
        </div>

        <TextLarge>{t(`motorsTour.slides.${currentSlideName}.description`)}</TextLarge>
        <div className={Classes.pagerDots} role="tablist">
          {SLIDE_NAMES.map((name, i) => (
            <button
              key={name}
              role="tab"
              aria-selected={i === currentSlide}
              aria-label={t('motorsTour.pagerDotLabel', { page: String(i + 1) })}
              className={`${Classes.dot}${i === currentSlide ? ` ${Classes.dotActive}` : ''}`}
              onClick={() => setCurrentSlide(i)}
            />
          ))}
        </div>
      </div>
    </BasicModalWithFooter>
  );
};

export default MotorsTourModal;
